import PersonIcon from '@mui/icons-material/Person';
import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { updateUser } from 'lib/api';
import { assignRoleToUser } from 'lib/api/users/roles';
import {
  COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PROFESSION_OPTIONS,
} from 'lib/consts/usersFields';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import {
  isValidChileanPhone,
  rutChanger,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import { buildSignerValidation, isEnumKey } from 'lib/functions/utils';
import { Role } from 'lib/types/models';
import React from 'react';
import { useDispatch } from 'react-redux';

import { UserInterface } from '../..';
import { nullUserState } from '../..';

interface Props {
  handleCloseDialog: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  openEditDialog: boolean;
  editType: 'password' | 'info';
  fetchUsers: () => Promise<void>;
  roles: Role[];
  setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
  selectedUser: UserInterface;
  setSelectedUser: React.Dispatch<React.SetStateAction<UserInterface>>;
  hasDigitalSignature: boolean;
}

const EditUserDialog: React.FC<Props> = ({
  handleCloseDialog,
  loading,
  setLoading,
  openEditDialog,
  editType,
  fetchUsers,
  roles,
  setSelectedUser,
  selectedUser,
  hasDigitalSignature,
}) => {
  const dispatch = useDispatch();
  const { hasError: identifierHasError, message: identifierErrorMessage } =
    buildSignerValidation(
      !!selectedUser.isSigner && !selectedUser?.identifier?.length,
      !!selectedUser?.identifier?.length && !validaRut(selectedUser.identifier),
      'Campo obligatorio',
      'Formato inválido (12345678-K)',
    );

  const { hasError: phoneHasError, message: phoneErrorMessage } =
    buildSignerValidation(
      !!selectedUser.isSigner && !selectedUser?.phone?.length,
      !!selectedUser?.phone?.length && !isValidChileanPhone(selectedUser.phone),
      'Campo obligatorio',
      'Teléfono inválido (569XXXXXXXX)',
    );

  const { hasError: countryHasError, message: countryErrorMessage } =
    buildSignerValidation(
      !!selectedUser.isSigner && !selectedUser?.country?.length,
      !!selectedUser?.country?.length &&
        !isEnumKey(COUNTRY_OPTIONS, selectedUser.country),
      'Campo obligatorio',
      'País inválido',
    );

  const { hasError: professionHasError, message: professionErrorMessage } =
    buildSignerValidation(
      !!selectedUser.isSigner && !selectedUser?.profession?.length,
      false,
      'Campo obligatorio',
      'Profesión inválida',
    );

  const { hasError: maritalHasError, message: maritalErrorMessage } =
    buildSignerValidation(
      !!selectedUser.isSigner && !selectedUser?.maritalStatus?.length,
      !!selectedUser?.maritalStatus?.length &&
        !isEnumKey(MARITAL_STATUS_OPTIONS, selectedUser.maritalStatus),
      'Campo obligatorio',
      'Estado civil inválido',
    );

  const hasErrors =
    phoneHasError ||
    identifierHasError ||
    countryHasError ||
    professionHasError ||
    maritalHasError;

  const handleSaveEditDialog = async () => {
    try {
      if (selectedUser.isSigner) {
        if (hasErrors) {
          let errorMessage = '';
          if (phoneHasError) errorMessage = phoneErrorMessage;
          else if (identifierHasError) errorMessage = identifierErrorMessage;
          else if (countryHasError) errorMessage = countryErrorMessage;
          else if (professionHasError) errorMessage = professionErrorMessage;
          else if (maritalHasError) errorMessage = maritalErrorMessage;
          showAlert(dispatch, 'error', errorMessage);
          return;
        }
      }
      setLoading(true);
      const payload = {
        id: selectedUser.id,
        name: selectedUser.name,
        identifier: selectedUser.identifier,
        phone: selectedUser.phone,
        country: selectedUser.country,
        profession: selectedUser.profession,
        maritalStatus: selectedUser.maritalStatus,
        isSigner: selectedUser.isSigner,
        ...(selectedUser.password?.length && {
          password: selectedUser.password,
        }),
      };
      await updateUser(dispatch, payload);
      setSelectedUser(nullUserState);
      await fetchUsers();
      handleCloseDialog();
      showAlert(dispatch, 'success', 'Se ha editado el usuario.');
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignationsEditDialog = async () => {
    try {
      setLoading(true);
      const rolesIds = (selectedUser.rolesNames || [])
        .map(
          (roleName: string) =>
            roles.find((role: Role) => role.name === roleName)?.id,
        )
        .filter((id): id is number => typeof id === 'number');
      const payload = {
        id: selectedUser.id,
        rolesIds,
      };
      await assignRoleToUser(dispatch, payload);
      setSelectedUser(nullUserState);
      await fetchUsers();
      handleCloseDialog();
      showAlert(
        dispatch,
        'success',
        'Se ha editado el usuario. Recuerda que con esto cambian las notificaciones, para revisarlas el usuario puede ir a su perfil.',
      );
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUser((prev: UserInterface) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    setSelectedUser((prev: UserInterface) => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleAutocompleteChange =
    (field: keyof UserInterface) =>
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: UserInterface[keyof UserInterface] | null,
    ) => {
      setSelectedUser((prev: UserInterface) => ({
        ...prev,
        [field]: value ?? prev[field],
      }));
    };

  const handleRutChange = (value: string) => {
    rutChanger(value, (formatted: string) =>
      setSelectedUser((prev: UserInterface) => ({
        ...prev,
        identifier: formatted,
      })),
    );
  };

  const getLabel = (value: string, options: Record<string, string>) => {
    return options[value] ?? '';
  };

  if (loading) return <CenteredCircularProgress />;

  return (
    <Dialog fullWidth open={openEditDialog} onClose={() => handleCloseDialog()}>
      <DialogTitle>
        <Title
          title={`Editar ${selectedUser.email}`}
          icon={<PersonIcon color="primary" />}
          color="primary"
        />
      </DialogTitle>
      {editType === 'password' ? (
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="name"
                label="Nombre"
                fullWidth
                value={selectedUser.name}
                onChange={handleInputChange}
              />
            </Grid>
            {hasDigitalSignature && (
              <Grid container item spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    name="identifier"
                    label="RUT"
                    value={selectedUser.identifier}
                    onChange={(e) => handleRutChange(e.target.value)}
                    fullWidth
                    error={identifierHasError}
                    helperText={
                      identifierHasError ? identifierErrorMessage : ''
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    name="phone"
                    label="Teléfono"
                    value={selectedUser.phone}
                    onChange={handleInputChange}
                    fullWidth
                    error={phoneHasError}
                    helperText={phoneHasError ? phoneErrorMessage : ''}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={Object.keys(COUNTRY_OPTIONS)}
                    value={selectedUser.country}
                    onChange={handleAutocompleteChange('country')}
                    getOptionLabel={(option) =>
                      getLabel(option, COUNTRY_OPTIONS)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="País"
                        margin="dense"
                        fullWidth
                        error={countryHasError}
                        helperText={countryHasError ? countryErrorMessage : ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    freeSolo
                    options={PROFESSION_OPTIONS}
                    value={selectedUser.profession || ''}
                    onChange={handleAutocompleteChange('profession')}
                    onInputChange={(_event, newInputValue, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        setSelectedUser((prev: UserInterface) => ({
                          ...prev,
                          profession: newInputValue,
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Profesión"
                        margin="dense"
                        fullWidth
                        error={professionHasError}
                        helperText={
                          professionHasError ? professionErrorMessage : ''
                        }
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value) {
                            setSelectedUser((prev: UserInterface) => ({
                              ...prev,
                              profession: value,
                            }));
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={Object.keys(MARITAL_STATUS_OPTIONS)}
                    value={selectedUser.maritalStatus || ''}
                    onChange={handleAutocompleteChange('maritalStatus')}
                    getOptionLabel={(option) =>
                      getLabel(option, MARITAL_STATUS_OPTIONS)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Estado civil"
                        margin="dense"
                        fullWidth
                        error={maritalHasError}
                        helperText={maritalHasError ? maritalErrorMessage : ''}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="password"
                label="Contraseña"
                value={selectedUser.password}
                onChange={handleInputChange}
                type="password"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              {hasDigitalSignature && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUser.isSigner}
                      onChange={(_, checked) =>
                        setSelectedUser((prev: UserInterface) => ({
                          ...prev,
                          isSigner: checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Asignar como firmante"
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
      ) : (
        <DialogContent style={{ paddingTop: 20 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-mutiple-checkbox-label">Permisos</InputLabel>
            <Select
              labelId="demo-mutiple-checkbox-label"
              id="demo-mutiple-checkbox"
              label="Permisos"
              multiple
              value={selectedUser.rolesNames}
              name="rolesNames"
              onChange={handleSelectChange}
              input={<Input />}
              renderValue={(selected) => selected.join(', ')}
            >
              {roles.map((role: Role) => (
                <MenuItem key={role.id} value={role.name}>
                  <Checkbox
                    checked={(selectedUser.rolesNames || []).includes(
                      role.name,
                    )}
                  />
                  <ListItemText primary={role.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={() =>
            editType === 'password'
              ? handleSaveEditDialog()
              : handleSaveAssignationsEditDialog()
          }
        >
          Guardar
        </CustomButton>
        <CustomButton onClick={() => handleCloseDialog()} color="secondary">
          Salir
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
